import { Card, Text, Group, Button, Box, Divider, Textarea, NumberInput } from '@mantine/core';

export interface GroupPreferencesSectionProps {
  maxGroups: number | null;
  isFlexibleGroups: boolean;
  sessionCount: number;
  additionalNotes: string;
  onMaxGroupsChange: (value: number | null) => void;
  onFlexibleGroupsChange: (value: boolean) => void;
  onNotesChange: (value: string) => void;
  perspective?: 'coach' | 'cm';
}

export function GroupPreferencesSection({
  maxGroups,
  isFlexibleGroups,
  sessionCount,
  additionalNotes,
  onMaxGroupsChange,
  onFlexibleGroupsChange,
  onNotesChange,
  perspective = 'coach',
}: GroupPreferencesSectionProps) {
  const isCm = perspective === 'cm';
  const totalSessions = maxGroups ? maxGroups * sessionCount : 0;

  const handleNumberChange = (value: string | number) => {
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!isNaN(num) && num >= 1) {
      onMaxGroupsChange(num);
      onFlexibleGroupsChange(false);
    } else if (value === '') {
      onMaxGroupsChange(null);
    }
  };

  const handleFlexibleClick = () => {
    onFlexibleGroupsChange(!isFlexibleGroups);
    if (!isFlexibleGroups) {
      onMaxGroupsChange(null);
    }
  };

  return (
    <Card withBorder padding="xl" radius="md">
      <Box mb={isCm ? undefined : 'lg'}>
        <Text fw={600} mb="xs">
          {isCm
            ? 'How many groups is the coach willing to take on?'
            : 'How many groups would you be willing to take on?'}{' '}
          <Text component="span" c="red">
            *
          </Text>
        </Text>
        <Text size="sm" c="dimmed" mb="md">
          {isCm
            ? 'Select a number or indicate flexibility.'
            : "Select a number or indicate you're flexible."}
        </Text>
        <Group gap="md" align="flex-start">
          <NumberInput
            value={isFlexibleGroups ? '' : (maxGroups ?? '')}
            onChange={handleNumberChange}
            min={1}
            step={1}
            allowDecimal={false}
            allowNegative={false}
            placeholder="# of groups"
            disabled={isFlexibleGroups}
            w={150}
            styles={{
              input: {
                fontWeight: 500,
                opacity: isFlexibleGroups ? 0.5 : 1,
              },
            }}
          />
          <Button
            variant={isFlexibleGroups ? 'filled' : 'outline'}
            color={isFlexibleGroups ? 'dark' : 'gray'}
            onClick={handleFlexibleClick}
            h={36}
            styles={{
              label: { fontWeight: 500 },
            }}
          >
            As many as needed
          </Button>
        </Group>
        <Box mt="sm">
          {isFlexibleGroups ? (
            <Text size="xs" c="dimmed">
              Based on program needs
            </Text>
          ) : maxGroups ? (
            <Text size="xs" c="dimmed">
              {maxGroups} group{maxGroups !== 1 ? 's' : ''} × {sessionCount} sessions = {totalSessions} total sessions
            </Text>
          ) : null}
        </Box>
      </Box>

      {!isCm && (
        <>
          <Divider mb="lg" />

          <Box>
            <Text fw={600} mb="xs">
              Anything else you'd like us to know? (optional)
            </Text>
            <Textarea
              value={additionalNotes}
              onChange={e => onNotesChange(e.target.value)}
              placeholder="Share any additional context, preferences, or relevant experience..."
              minRows={4}
              autosize
            />
          </Box>
        </>
      )}
    </Card>
  );
}
